import { useEffect, useState } from 'react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#001E3C]">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="https://www.hi-tak.co.jp/wp-content/uploads/2022/03/ハイタック動画ショート_1.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
      </div>

      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(90deg, #00D9C9 1px, transparent 1px),
            linear-gradient(0deg, #00D9C9 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
          animation: 'gridMove 20s linear infinite'
        }}></div>
      </div>

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#001E3C]/80 via-[#002A4E]/60 to-[#001E3C]/80"></div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="inline-block mb-6">
              <span className="text-[#00D9C9] text-sm font-semibold tracking-wider uppercase border border-[#00D9C9] px-4 py-2 rounded-full">
                Advanced Manufacturing Technology
              </span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Precision Deep Hole
              <span className="block text-[#00D9C9]">Drilling Solutions</span>
            </h1>
            
            <p className="text-xl text-white/80 mb-8 leading-relaxed max-w-xl">
              Leading the industry with cutting-edge deep hole drilling, honing, and gun drilling technologies. Transforming manufacturing across medical, automotive, and industrial sectors.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="#services"
                className="group bg-[#00D9C9] text-[#001E3C] px-8 py-4 rounded font-bold hover:bg-[#00F5E0] transition-all duration-300 text-lg flex items-center gap-2 whitespace-nowrap"
              >
                Explore Solutions
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform w-6 h-6 flex items-center justify-center"></i>
              </a>
              <a
                href="#contact"
                className="group bg-transparent text-white border-2 border-white/30 px-8 py-4 rounded font-bold hover:border-[#00D9C9] hover:text-[#00D9C9] transition-all duration-300 text-lg flex items-center gap-2 whitespace-nowrap"
              >
                Contact Us
                <i className="ri-mail-line text-xl w-6 h-6 flex items-center justify-center"></i>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-white/10">
              <div>
                <div className="text-4xl font-bold text-[#00D9C9] mb-2">30+</div>
                <div className="text-sm text-white/70">Years Experience</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#00D9C9] mb-2">10,000+</div>
                <div className="text-sm text-white/70">Projects Completed</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#00D9C9] mb-2">98%</div>
                <div className="text-sm text-white/70">Client Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Right Content - Image */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="relative">
              {/* Decorative Elements */}
              <div className="absolute -top-8 -right-8 w-64 h-64 border-2 border-[#00D9C9]/30 rounded-lg"></div>
              <div className="absolute -bottom-8 -left-8 w-64 h-64 border-2 border-[#00D9C9]/30 rounded-lg"></div>
              
              {/* Main Image */}
              <div className="relative z-10 rounded-lg overflow-hidden shadow-2xl">
                <div className="w-full h-[600px]">
                  <img
                    src="https://www.hi-tak.co.jp/wp-content/uploads/2021/01/DSCF1373-scaled.jpg"
                    alt="Advanced Deep Hole Drilling Technology"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#001E3C] via-transparent to-transparent"></div>
              </div>

              {/* Floating Card */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg p-6 z-20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#00D9C9] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-2xl text-[#001E3C]"></i>
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">Industry 4.0 Ready</div>
                    <div className="text-white/70 text-sm">Smart manufacturing solutions</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/60 text-xs uppercase tracking-wider">Scroll</span>
          <i className="ri-arrow-down-line text-[#00D9C9] text-2xl w-6 h-6 flex items-center justify-center"></i>
        </div>
      </div>

      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(80px, 80px); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
